import * as T from 'three';
import type { createSceneKit } from '@london-life/game/scene/kit';
import type { createProps } from '@london-life/game/city/props';
import { KINGS_CROSS, LAND, siteAt, type StationSite } from '@london-life/game/city/layout';
import type { MapPoint } from '@london-life/game/city/types';

// King's Cross on its slab joined to the map's north edge (layout.ts), drawn the way the airports are: the
// yellow-brick front with its two great arched windows and the clock tower between, the two glazed train sheds
// over the platforms, the tracks fanning out of them to the slab's edge, trains waiting at the platforms, the
// Western Concourse's white lattice dome, the Great Northern Hotel, King's Cross Square with its trees, the
// gasholders' iron frames and St Pancras's red-brick tower next door, and the road in to the city's streets.

type Kit = ReturnType<typeof createSceneKit>;
type Props = ReturnType<typeof createProps>;
export type TrainPaint = { body: string; band: string; doors: string };

const BRICK = '#d6c08a';
const BRICK_DARK = '#b9a26c';
const RED_BRICK = '#a5503c';
const GLASS = '#c9d6dd';
const STEEL = '#4b545c';
const RAIL = '#7b8086';
const BALLAST = '#8f877a';
const PLATFORM = '#cfccc4';

// a train on the map: cars in its line's paint with the band along the windows, a sloping nose at each end
// (bigger than true to the map, as the jets are), along +x
export function buildMapTrain(kit: Kit, paint: TrainPaint, cars = 5) {
  const group = new T.Group();
  const car = 4.4;
  const length = car * cars;
  for (let i = 0; i < cars; i += 1) {
    const x = -length / 2 + car * (i + 0.5);
    kit.box(car - 0.12, 1.35, 1.5, paint.body, x, 0.95, 0, group);
    kit.box(car - 0.12, 0.34, 1.52, paint.band, x, 1.15, 0, group);
    for (const side of [-1, 1]) kit.box(0.5, 0.95, 0.03, paint.doors, x, 0.8, side * 0.77, group);
    kit.box(car - 0.3, 0.08, 1.2, '#5b636b', x, 1.66, 0, group);
  }
  for (const end of [-1, 1]) {
    const nose = kit.box(0.9, 0.9, 1.48, paint.band, end * (length / 2 + 0.3), 0.75, 0, group);
    nose.rotation.z = end * -0.35;
    kit.box(0.05, 0.4, 1.1, '#1d2731', end * (length / 2 + 0.05), 1.3, 0, group);
  }
  return { group, length };
}

// the tracks out of the station: one per platform, from the buffers in the shed to the slab's north edge (each
// meets its neighbours at the throat), as map points; the trains on the map run along them
export function stationTracks(site: StationSite = KINGS_CROSS): MapPoint[][] {
  const tracks: MapPoint[][] = [];
  const { sheds } = site;
  const out = site.tracks;
  sheds.x.forEach((shedX, s) => {
    for (let i = 0; i < out; i += 1) {
      const dx = shedX - sheds.w / 2 + (sheds.w * (i + 0.5)) / out;
      // at the throat the station's tracks close up into four running lines
      const lane = (s * out + i) % 4;
      const exit = -3 + lane * 2;
      tracks.push([
        siteAt(site, [dx, sheds.from - 2]),
        siteAt(site, [dx, sheds.to]),
        siteAt(site, [exit, site.throat]),
        siteAt(site, [exit, -site.depth / 2]),
      ]);
    }
  });
  return tracks;
}

export function buildStation(kit: Kit, props: Props, group: T.Group) {
  const { box, tree, road } = props;
  const site = KINGS_CROSS;
  const at = (dx: number, dz: number) => siteAt(site, [dx, dz]);
  const streets: [MapPoint, MapPoint][] = [];
  const owned: { dispose: () => void }[] = [];
  const flat = (w: number, d: number, color: string, x: number, z: number, y = 0.2, h = 0.12, turn = 0) =>
    box(w, h, d, color, x, y, z, turn);
  const leg = (a: MapPoint, b: MapPoint) => ({
    middle: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] as MapPoint,
    length: Math.hypot(b[0] - a[0], b[1] - a[1]),
    turn: -Math.atan2(b[1] - a[1], b[0] - a[0]),
  });
  const own = <M extends T.Material>(material: M) => (owned.push(material), material);

  // ---- the slab and its grass
  box(site.width + 0.6, 1.1, site.depth + 0.6, LAND.edge, site.x, -0.5, site.z);
  box(site.width, 0.12, site.depth, '#9fc883', site.x, 0.06, site.z);

  // ---- the tracks: ballast with two rails, out of the sheds, through the throat and off the slab
  const tracks = stationTracks(site);
  for (const track of tracks)
    for (let i = 1; i < track.length; i += 1) {
      const { middle, length, turn } = leg(track[i - 1]!, track[i]!);
      flat(length + 0.6, 1.5, BALLAST, middle[0], middle[1], 0.19, 0.1, turn);
      const across = new T.Object3D();
      across.position.set(middle[0], 0, middle[1]);
      across.rotation.y = turn;
      group.add(across);
      for (const side of [-0.38, 0.38]) kit.box(length + 0.6, 0.08, 0.1, RAIL, 0, 0.29, side, across);
    }
  // the overhead wires' masts along the running lines
  for (let dz = site.throat; dz > -site.depth / 2 + 2; dz -= 5) {
    const [x, z] = at(-5.2, dz);
    const [x2] = at(5.2, dz);
    box(0.2, 3.2, 0.2, STEEL, x, 1.7, z);
    box(0.2, 3.2, 0.2, STEEL, x2, 1.7, z);
    box(x2 - x, 0.14, 0.14, STEEL, (x + x2) / 2, 3.3, z);
  }

  // ---- the platforms under the sheds, between the tracks, and the trains waiting at some of them
  const { sheds } = site;
  const shedLength = sheds.from - sheds.to;
  const shedMiddle = (sheds.from + sheds.to) / 2;
  const trackGap = sheds.w / site.tracks;
  for (const shedX of sheds.x)
    for (let i = 0; i <= site.tracks; i += 2) {
      const dx = shedX - sheds.w / 2 + trackGap * i;
      const [x, z] = at(dx, shedMiddle + 1);
      flat(i === 0 || i === site.tracks ? 1 : 1.6, shedLength - 2, PLATFORM, x, z, 0.3, 0.4);
      // the yellow lines at the edges
      flat(0.12, shedLength - 2, '#e8c23a', x - 0.7, z, 0.51, 0.02);
      flat(0.12, shedLength - 2, '#e8c23a', x + 0.7, z, 0.51, 0.02);
    }
  // the trains waiting at some of the platforms, in the lines' paint (set once the content is known)
  const parked = new T.Group();
  group.add(parked);
  const setTrains = (paints: TrainPaint[]) => {
    parked.clear();
    if (!paints.length) return;
    [0, 3, 5, 6].forEach((index, k) => {
      const track = tracks[index];
      if (!track) return;
      const train = buildMapTrain(kit, paints[k % paints.length]!);
      const [a, b] = [track[0]!, track[1]!];
      train.group.position.set(a[0] + (b[0] - a[0]) * 0.42, 0.25, a[1] + (b[1] - a[1]) * 0.42);
      train.group.rotation.y = Math.atan2(-(b[1] - a[1]), b[0] - a[0]);
      parked.add(train.group);
    });
  };

  // ---- the two train sheds: barrel roofs of glass on iron ribs, yellow-brick walls along the sides
  const roofGlass = own(
    new T.MeshStandardMaterial({ color: GLASS, roughness: 0.25, metalness: 0.2, transparent: true, opacity: 0.55 }),
  );
  roofGlass.side = T.DoubleSide;
  const r = sheds.w / 2;
  const barrel = kit.geo(`shed${r},${shedLength}`, () =>
    new T.CylinderGeometry(r, r, shedLength, 18, 1, true, -Math.PI / 2, Math.PI).rotateX(-Math.PI / 2),
  );
  const rib = kit.geo(`rib${r}`, () => new T.TorusGeometry(r, 0.12, 4, 16, Math.PI));
  for (const shedX of sheds.x) {
    const [x, z] = at(shedX, shedMiddle);
    const roof = new T.Mesh(barrel, roofGlass);
    roof.position.set(x, 3.2, z);
    group.add(roof);
    for (let k = 0; k <= 8; k += 1) {
      const ring = new T.Mesh(rib, kit.mat(STEEL));
      const [, rz] = at(shedX, sheds.to + (shedLength * k) / 8);
      ring.position.set(x, 3.2, rz);
      group.add(ring);
    }
    for (const side of [-1, 1]) box(0.6, 3.2, shedLength, BRICK_DARK, x + side * (r - 0.1), 1.8, z);
  }

  // ---- the front: yellow brick, two great arched windows (one on each shed), the clock tower between
  const front = site.front;
  const [fx, fz] = at(front.x, front.z);
  box(front.w, front.h, 2, BRICK, fx, front.h / 2 + 0.2, fz);
  box(front.w + 0.4, 0.4, 2.2, BRICK_DARK, fx, front.h + 0.4, fz);
  const archGlass = kit.mat('#2f3a45');
  for (const shedX of sheds.x) {
    const [ax] = at(shedX, front.z);
    const arch = new T.Mesh(
      kit.geo('arch5', () => new T.CircleGeometry(5, 18, 0, Math.PI)),
      archGlass,
    );
    arch.position.set(ax, 3.2, fz + 1.02);
    group.add(arch);
    box(10, 3, 0.05, '#2f3a45', ax, 1.7, fz + 1.02);
    // the arch's brick ring
    const ring = new T.Mesh(
      kit.geo('archRing', () => new T.TorusGeometry(5.1, 0.25, 4, 18, Math.PI)),
      kit.mat(BRICK_DARK),
    );
    ring.position.set(ax, 3.2, fz + 1.05);
    group.add(ring);
  }
  // the clock tower and its four clocks
  box(3, 12, 3, BRICK, fx, 6.2, fz - 0.2);
  box(3.4, 0.4, 3.4, BRICK_DARK, fx, 12.4, fz - 0.2);
  kit.cone(2.2, 2, '#55616a', fx, 13.6, fz - 0.2, group, 4);
  for (const [dx, dz, turn] of [
    [0, 1.55, 0],
    [0, -1.95, Math.PI],
    [1.55, -0.2, Math.PI / 2],
    [-1.55, -0.2, -Math.PI / 2],
  ] as const) {
    const face = new T.Mesh(
      kit.geo('clock', () => new T.CircleGeometry(0.9, 18)),
      kit.mat('#f7f3e6'),
    );
    face.position.set(fx + dx, 10.4, fz + dz);
    face.rotation.y = turn;
    group.add(face);
    const hand = kit.box(0.1, 0.7, 0.04, '#1d2731', 0, 0.2, 0.02, face);
    hand.rotation.z = 0.6;
  }

  // ---- the Western Concourse: a white lattice dome over the ticket hall, beside the front
  const hall = site.concourse;
  const [cx, cz] = at(hall.x, hall.z);
  const dome = kit.dome(hall.r, '#eef1f3', cx, 0.2, cz, group);
  dome.scale.y = 0.42;
  const lattice = own(new T.MeshBasicMaterial({ color: '#9aa4ab', wireframe: true }));
  const web = new T.Mesh(
    kit.geo(`lattice${hall.r}`, () => new T.SphereGeometry(hall.r + 0.05, 16, 6, 0, Math.PI * 2, 0, Math.PI / 2)),
    lattice,
  );
  web.position.set(cx, 0.2, cz);
  web.scale.y = 0.42;
  group.add(web);
  // the Great Northern Hotel curving behind the concourse
  const hotel = site.hotel;
  const [hx, hz] = at(hotel.x, hotel.z);
  box(hotel.w, hotel.h, hotel.d, BRICK, hx, hotel.h / 2 + 0.2, hz, 0.35);
  box(hotel.w + 0.3, 0.6, hotel.d + 0.3, '#5d6770', hx, hotel.h + 0.5, hz, 0.35);

  // ---- King's Cross Square: pale paving, trees, the road in
  const square = site.square;
  const [sx, sz] = at(square.x, square.z);
  flat(square.w, square.d, '#e2dccd', sx, sz, 0.19, 0.12);
  for (let k = -0.4; k <= 0.41; k += 0.2) tree(sx + k * square.w, sz + square.d / 2 - 1.2, 0.7, '#4f8f48');
  const path = [...site.road.map(([dx, dz]) => at(dx, dz)), ...site.joins];
  for (let i = 1; i < path.length; i += 1) {
    const { middle, length, turn } = leg(path[i - 1]!, path[i]!);
    road(middle[0], middle[1], length, turn, 4.2);
    streets.push([path[i - 1]!, path[i]!]);
  }
  // the drop-off road along the front
  road(sx, sz + square.d / 2 + 2, square.w, 0, 3.6);
  streets.push([at(-square.w / 2, square.z + square.d / 2 + 2), at(square.w / 2, square.z + square.d / 2 + 2)]);

  // ---- the gasholders: round iron frames, the park inside one, flats inside the others
  const frame = own(new T.MeshBasicMaterial({ color: '#5f6f6a', wireframe: true }));
  site.gasholders.forEach(([dx, dz], k) => {
    const [x, z] = at(dx, dz);
    const cage = new T.Mesh(
      kit.geo('gasholder', () => new T.CylinderGeometry(4.2, 4.2, 6, 16, 3, true)),
      frame,
    );
    cage.position.set(x, 3.2, z);
    group.add(cage);
    if (k === 0) kit.cylinder(4, 0.2, '#86b56d', x, 0.3, z, group, 4, 16);
    else kit.cylinder(3.4, 4.6 + k, '#cfd4d6', x, (4.6 + k) / 2 + 0.2, z, group, 3.4, 16);
  });

  // ---- St Pancras next door: red brick, the tall clock tower with its spire, its own great shed behind
  const pancras = site.pancras;
  const [px, pz] = at(pancras.x, pancras.z);
  box(pancras.w, pancras.h, 4, RED_BRICK, px, pancras.h / 2 + 0.2, pz + pancras.d / 2 - 2);
  box(2.4, 13, 2.4, RED_BRICK, px - pancras.w / 2 + 1.6, 6.7, pz + pancras.d / 2 - 2);
  kit.cone(1.6, 4, '#4a5560', px - pancras.w / 2 + 1.6, 15.2, pz + pancras.d / 2 - 2, group, 4);
  const big = new T.Mesh(
    kit.geo(`pancras${pancras.w}`, () =>
      new T.CylinderGeometry(pancras.w / 2, pancras.w / 2, pancras.d - 4, 18, 1, true, -Math.PI / 2, Math.PI).rotateX(
        -Math.PI / 2,
      ),
    ),
    roofGlass,
  );
  big.position.set(px, 3.4, pz - 2);
  group.add(big);
  for (const side of [-1, 1]) box(0.6, 3.4, pancras.d - 4, RED_BRICK, px + side * (pancras.w / 2 - 0.2), 1.9, pz - 2);

  // ---- the name on the grass, trees along the slab's south edge
  const [lx, lz] = at(...site.label);
  kit.label(site.name, lx, lz, group, 30, '#4f6b45');
  for (let k = 0; k < 12; k += 1) {
    const t = (k + 0.5) / 12;
    const [x, z] = at(-site.width / 2 + t * site.width, site.depth / 2 - 2.5);
    if (Math.abs(x - path[1]![0]) > 5) tree(x, z, 0.75, k % 2 ? '#5f9b52' : '#4f8f48');
  }

  return {
    streets,
    tracks,
    setTrains,
    dispose: () => owned.forEach((item) => item.dispose()),
  };
}
